import { useEffect } from 'react';
import useIdentity from '../useIdentity';
import { useProductSettings, type ProductSettings } from '@workspace/utils-react';
import { AuthProvider, type AuthUserProfile } from '../../../resource-scheduler/src/providers/AuthProvider';
import { configureAuth } from '../../../resource-scheduler/src/lib/auth-fetch';
import type { ReactNode } from 'react';

interface AuthBridgeProviderProps {
  instanceId?: string;
  children: ReactNode;
}

export function AuthBridgeProvider({ instanceId, children }: AuthBridgeProviderProps): React.ReactElement {
  const { getAccessToken, signin, loading, error, isAuthenticated, user } = useIdentity();
  const settings = useProductSettings<ProductSettings>();

  useEffect(() => {
    if (!isAuthenticated && !loading && !error) {
      signin();
    }
  }, [isAuthenticated, loading, error, signin]);

  useEffect(() => {
    if (error && (error as any)?.message === 'login_required' && settings?.workspaceUrl) {
      const message = {
        type: 'RequestReauthentication',
      };
      window.parent.postMessage(JSON.stringify(message), settings.workspaceUrl);
    }
  }, [error, settings?.workspaceUrl]);

  // Read evoOrganisationId from sessionStorage (set by useAuthenticatedEmployee hook)
  const evoOrganisationId = sessionStorage.getItem('evo_organisation_id') || undefined;

  // Configure authFetch with the getAccessToken function from useIdentity
  // This ensures auth is configured before resource-scheduler components render
  useEffect(() => {
    configureAuth({ getAccessToken, instanceId, evoOrganisationId });
  }, [getAccessToken, instanceId, evoOrganisationId]);

  if (loading) {
    return (
      <div className="loading-container">
        <p>Loading authentication...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <h2>Authentication Error: {error?.message}</h2>
      </div>
    );
  }

  if (isAuthenticated && user) {
    // Build display name: prefer given_name + family_name; fall back to
    // profile.name only when it does NOT look like an email address.
    const givenFamily = `${user.profile?.given_name || ''} ${user.profile?.family_name || ''}`.trim();
    const profileName = user.profile?.name && !user.profile.name.includes('@') ? user.profile.name : '';
    const displayName = givenFamily || profileName || '';

    const userProfile: AuthUserProfile | undefined = user.profile
      ? {
          id: user.profile.sub,
          name: displayName,
          email: (user.profile.email as string) || '',
        }
      : undefined;

    return (
      <AuthProvider getAccessToken={getAccessToken} instanceId={instanceId} isProduction={true} evoOrganisationId={evoOrganisationId} userProfile={userProfile}>
        {children}
      </AuthProvider>
    );
  }

  return (
    <div className="unauthenticated-container">
      <p>Authentication required...</p>
    </div>
  );
}
